import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import { Users, Package, Heart, EnvelopeSimple, ChartBar, CurrencyDollar, Lock, Sparkle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatTile({ label, value, icon: Icon, color = "#002FA7", testid }) {
  return (
    <div className="bg-white p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="pg-overline">{label}</div>
          <div className="font-heading text-4xl font-black tracking-tighter mt-3" style={{ color }}>{value}</div>
        </div>
        <Icon size={26} weight="duotone" color={color} />
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [params, setParams] = useSearchParams();
  const [tokenInput, setTokenInput] = useState(params.get("token") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async (tok) => {
    if (!tok) { setError("Token required."); return; }
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/admin/analytics?token=${encodeURIComponent(tok)}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.status === 403 ? "Token rejected - check ADMIN_TOKEN." : (e?.response?.data?.detail || "Failed to load."));
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = params.get("token");
    if (t) fetchData(t);
    // eslint-disable-next-line
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setParams({ token: tokenInput });
    fetchData(tokenInput);
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-white" data-testid="admin-gate">
        <Header />
        <main className="max-w-[700px] mx-auto px-6 py-20">
          <div className="pg-overline mb-3">Internal · Owner-only</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">Admin analytics<span className="text-[#002FA7]">.</span></h1>
          <p className="text-[#52525b] mt-4">Paste the admin token to view live metrics. This page is not linked anywhere - bookmark it.</p>
          <form onSubmit={submit} className="mt-8 flex gap-2">
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="ADMIN_TOKEN…"
              className="flex-1 border border-[#0a0a0a] py-3 px-4 outline-none focus:border-[#002FA7] text-sm font-mono-pg"
              data-testid="admin-token-input"
              autoFocus
            />
            <button type="submit" disabled={loading || !tokenInput} className="bg-[#002FA7] text-white font-bold px-6 py-3 hover:bg-[#00207A] disabled:opacity-60 inline-flex items-center gap-2" data-testid="admin-token-submit">
              <Lock size={16} weight="bold" /> {loading ? "Unlocking…" : "Unlock"}
            </button>
          </form>
          {error && <div className="mt-4 text-sm text-[#FF3B30] font-mono-pg" data-testid="admin-token-error">{error}</div>}
          <p className="text-xs text-[#52525b] mt-6 font-mono-pg">Tip: pass the token in the URL - <code className="bg-[#f0f0f2] px-1.5 py-0.5">/admin?token=…</code></p>
        </main>
      </div>
    );
  }

  const t = data.totals;
  const max = Math.max(1, ...data.signups_last_30_days.map((s) => s.count));

  return (
    <div className="min-h-screen bg-white" data-testid="admin-analytics-page">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="pg-overline mb-2">Internal dashboard · {new Date(data.generated_at).toLocaleString("en-NZ")}</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">Operator analytics<span className="text-[#002FA7]">.</span></h1>
          </div>
          <button onClick={() => fetchData(tokenInput)} className="border border-[#0a0a0a] font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-[#0a0a0a] hover:text-white transition-colors" data-testid="admin-refresh">
            Refresh →
          </button>
        </div>

        {data.launch_free_pro && (
          <div className="bg-[#D97706] text-white px-5 py-3 mb-8 font-mono-pg text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="admin-launch-free-flag">
            <Sparkle size={12} weight="fill" /> Launch mode - Pro is currently free for all users (LAUNCH_FREE_PRO=true)
          </div>
        )}

        {/* Core stat grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mb-10">
          <StatTile label="Total users" value={t.users.toLocaleString("en-NZ")} icon={Users} testid="admin-stat-users" />
          <StatTile label="Pro users" value={t.pro_users.toLocaleString("en-NZ")} icon={Sparkle} color="#D97706" testid="admin-stat-pro" />
          <StatTile label="Tracked purchases" value={t.tracked_purchases.toLocaleString("en-NZ")} icon={Package} testid="admin-stat-purchases" />
          <StatTile label="Wishlist items" value={t.wishlist_items.toLocaleString("en-NZ")} icon={Heart} testid="admin-stat-wishlist" />
          <StatTile label="Waitlist signups" value={t.waitlist_signups.toLocaleString("en-NZ")} icon={EnvelopeSimple} testid="admin-stat-waitlist" />
          <StatTile label="Potential claim value" value={`$${(t.potential_claim_value_nzd || 0).toLocaleString("en-NZ", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`} icon={CurrencyDollar} color="#16A34A" testid="admin-stat-claim-value" />
        </div>

        {/* Signups chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mb-10">
          <div className="bg-white p-6" data-testid="admin-signups-chart">
            <div className="flex items-center gap-2 mb-4">
              <ChartBar size={18} weight="duotone" color="#002FA7" />
              <div className="pg-overline">Signups · last 30 days</div>
            </div>
            {data.signups_last_30_days.length === 0 ? (
              <div className="font-mono-pg text-sm text-[#52525b]">No signups in the last 30 days.</div>
            ) : (
              <div className="flex items-end gap-1 h-40 mt-4">
                {data.signups_last_30_days.map((s) => (
                  <div key={s.date} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${s.date}: ${s.count}`}>
                    <div className="w-full bg-[#002FA7] hover:bg-[#00207A]" style={{ height: `${(s.count / max) * 100}%` }} />
                    <div className="font-mono-pg text-[9px] text-[#52525b] truncate w-full text-center">{s.date.slice(5)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white p-6" data-testid="admin-email-events">
            <div className="flex items-center gap-2 mb-4">
              <EnvelopeSimple size={18} weight="duotone" color="#002FA7" />
              <div className="pg-overline">Email events (all-time)</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
              {["delivered", "opened", "clicked", "bounced", "complained"].map((k) => (
                <div key={k} className="bg-white p-4">
                  <div className="pg-overline">{k}</div>
                  <div className="font-heading text-2xl font-black tracking-tighter mt-1" style={{ color: k === "bounced" || k === "complained" ? "#FF3B30" : "#0a0a0a" }}>
                    {(data.email_events[k] || 0).toLocaleString("en-NZ")}
                  </div>
                </div>
              ))}
            </div>
            {Object.keys(data.email_events).length === 0 && (
              <p className="mt-4 font-mono-pg text-xs text-[#52525b] leading-relaxed">
                No events recorded yet. Configure Resend → Webhooks to POST to <code className="bg-[#f0f0f2] px-1.5 py-0.5">/api/webhooks/resend-events</code> and subscribe to: delivered, opened, clicked, bounced, complained.
              </p>
            )}
          </div>
        </div>

        {/* Recent payments */}
        <div className="border border-[#e4e4e7] bg-white p-6" data-testid="admin-recent-payments">
          <div className="pg-overline mb-4">Recent fulfilled transactions (incl. admin grants)</div>
          {data.recent_payments.length === 0 ? (
            <div className="font-mono-pg text-sm text-[#52525b]">No transactions yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-payments-table">
              <thead className="text-left text-[#52525b] font-mono-pg text-xs uppercase tracking-widest">
                <tr><th className="py-2">When</th><th>User</th><th>Plan</th><th className="text-right">Amount</th></tr>
              </thead>
              <tbody>
                {data.recent_payments.map((p, i) => (
                  <tr key={i} className="border-t border-[#e4e4e7]">
                    <td className="py-3 font-mono-pg text-xs">{p.fulfilled_at ? new Date(p.fulfilled_at).toLocaleString("en-NZ") : "-"}</td>
                    <td className="py-3">{p.user_email}</td>
                    <td className="py-3 font-mono-pg text-xs uppercase tracking-widest">{p.plan_id}</td>
                    <td className="py-3 text-right font-mono-pg">{p.amount === 0 ? "COMP" : `$${Number(p.amount).toFixed(2)} ${(p.currency || "").toUpperCase()}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-xs font-mono-pg text-[#52525b] mt-10 max-w-2xl">
          This page is owner-only and not linked from anywhere in the public app. To install Google Analytics in addition, drop your GA4 measurement ID (G-XXXXXXXXXX) into <code className="bg-[#f0f0f2] px-1.5 py-0.5">REACT_APP_GA_MEASUREMENT_ID</code> in frontend/.env.
        </p>
      </main>
    </div>
  );
}
